import React, { forwardRef, useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Slide,
  styled,
  Switch,
  Typography,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
  Tabs,
  Tab,
} from "@mui/material";
import { CustomInput, IconContainer, PageTitle, PillRadio } from "../../components-new/common";
import { ScenarioIcon } from "../../components-new/icons";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import {
  BrowserOptions,
  EnvOptions,
  ResolutionOptions,
} from "../../components-new/utils/constants";
import { useSelector, useDispatch } from "react-redux";
import useSaveTestCaseSettingsQuery from "../../useQuery/useSaveTestCaseSettingsQuery";
import { useNotification } from "../../hooks";
import TestCaseSearch from "../../components-new/common/TestCaseSearch";
import {
  setPreProcessingConfig,
  setPostProcessingConfig,
  resetPreProcessingConfig,
  resetPostProcessingConfig,
} from "../../Redux/ProcessingSettings/ProcessingSettings";

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const CustomDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiPaper-root": {
    width: "900px",
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    textAlign: "auto",
    padding: "14px",
  },
  "& .MuiDialogContent-root": {
    overflowY: "scroll",
    paddingRight: "24px",
    width: "100%",
    "&::-webkit-scrollbar": {
      width: "8px",
      display: "block",
    },
    "&::-webkit-scrollbar-track": {
      background: "#f1f1f1",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#888",
      borderRadius: "4px",
      "&:hover": {
        background: "#555",
      },
    },
  },
}));

const StyledLabel = styled(Typography)`
  color: #8089a8;
  font-size: 13px;
  font-weight: 500;
`;

const StyledButton = styled(Button)`
  padding: 15px 28px;
  border-radius: 8px;
  text-transform: capitalize;
  text-align: center;
  font-size: 17px;
  font-weight: 600;
  line-height: 20px;
`;

const StyledTab = styled(Tab)({
  textTransform: "none",
  minWidth: 0,
  padding: "12px 16px",
  "&.Mui-selected": {
    color: "#ff5336",
  },
});

const TabPanel = ({ children, value, index }) => (
  <Box hidden={value !== index} sx={{ width: "100%", mt: 2 }}>
    {value === index && children}
  </Box>
);

const Settings = () => {
  const dispatch = useDispatch();
  const selectedTestCase = useSelector((state) => state.authoringSlice.selectedTestcaseItem);
  const { mutate: saveTestCaseSettings, isSuccess } = useSaveTestCaseSettingsQuery();
  const notify = useNotification();
  const preProcessingConfig = useSelector((state) => state.processingSettings.preProcessing);
  const postProcessingConfig = useSelector((state) => state.processingSettings.postProcessing);
  const isMobile = useSelector((state) => state.authoringSlice.isMobile);

  const [open, setOpen] = useState(false);
  const [selectedEnv, setSelectedEnv] = useState(selectedTestCase.envname);
  const [selectedBrowser, setSelectedBrowser] = useState(selectedTestCase.browser);
  const [selectedResolution, setSelectedResolution] = useState("");
  const [enableAudio, setEnableAudio] = useState(false);
  const [continueTest, setContinueTest] = useState(false);
  const [runOnce, setRunOnce] = useState(false);
  const [stealthMode, setStealthMode] = useState(false);
  const [threshold, setThreshold] = useState("");
  const [proxyUrl, setProxyUrl] = useState("");
  const [envUrl, setEnvUrl] = useState("");
  const [description, setDescription] = useState("");
  const [forceNewSession, setForceNewSession] = useState(false);
  const [keepBrowserOpen, setKeepBrowserOpen] = useState(false);

  const [processingTab, setProcessingTab] = useState(0);

  useEffect(() => {
    if (selectedTestCase) {
      setSelectedEnv(selectedTestCase?.envname ?? "");
      setSelectedBrowser(selectedTestCase?.browser ?? "");
      setEnableAudio(selectedTestCase?.enableaudio ?? false);
      setContinueTest(selectedTestCase?.continuetest ?? false);
      setRunOnce(selectedTestCase?.multirun === 0 ? true : false);
      setStealthMode(selectedTestCase?.stealthMode ?? false);
      setThreshold(selectedTestCase?.testcasethreshold ?? "");
      setProxyUrl(selectedTestCase?.proxyurl ?? "");
      setEnvUrl(selectedTestCase?.envUrl ?? "");
      setDescription(selectedTestCase?.description ?? "");
      setForceNewSession(selectedTestCase?.forcenewsession ?? false);
      setKeepBrowserOpen(selectedTestCase?.keepbrowseropen ?? false);

      dispatch(
        setPreProcessingConfig({
          testCase: selectedTestCase?.preProcessedTestCase || null,
          executeCondition:
            selectedTestCase?.executeConditionPre === "pass"
              ? "ifPass"
              : selectedTestCase?.executeConditionPre === "fail"
                ? "ifFail"
                : selectedTestCase?.executeConditionPre === "always"
                  ? "always"
                  : "",
        }),
      );
      dispatch(
        setPostProcessingConfig({
          testCase: selectedTestCase?.postProcessedTestCase || null,
          executeCondition:
            selectedTestCase?.executeConditionPost === "pass"
              ? "ifPass"
              : selectedTestCase?.executeConditionPost === "fail"
                ? "ifFail"
                : selectedTestCase?.executeConditionPost === "always"
                  ? "always"
                  : "",
        }),
      );
    }
  }, [selectedTestCase, dispatch]);

  useEffect(() => {
    if (isSuccess) {
      notify("Settings changes saved successfully", "success");
      handleClose();
      dispatch(resetPreProcessingConfig());
      dispatch(resetPostProcessingConfig());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, dispatch]);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleEnvChange = (event) => {
    setSelectedEnv(event.target.value);
  };

  const handleBrowserChange = (event) => {
    setSelectedBrowser(event.target.value);
  };

  const handleResolutionChange = (event) => {
    setSelectedResolution(event.target.value);
  };

  const handleTabChange = (event, newValue) => {
    setProcessingTab(newValue);
  };

  const handlePreProcessingTestCaseSelect = (testCase) => {
    dispatch(setPreProcessingConfig({ testCase }));
  };

  const handlePostProcessingTestCaseSelect = (testCase) => {
    dispatch(setPostProcessingConfig({ testCase }));
  };

  const handlePreProcessingConditionChange = (event) => {
    dispatch(setPreProcessingConfig({ executeCondition: event.target.value }));
  };

  const handlePostProcessingConditionChange = (event) => {
    dispatch(setPostProcessingConfig({ executeCondition: event.target.value }));
  };

  const handleSave = () => {
    const payload = {
      EnvironmentName: selectedEnv,
      Browser: selectedBrowser,
      Resolution: selectedResolution,
      RunOnce: runOnce ? 0 : 1,
      stealthMode: stealthMode,
      Threshold: threshold,
      Proxy: proxyUrl,
      testCaseId: selectedTestCase.idtest_case,
      enablevideo: enableAudio,
      continuetest: continueTest,
      description: description,
      forcenewsession: forceNewSession,
      keepbrowseropen: keepBrowserOpen,
      preProcessedTestCase: {
        testCaseId: preProcessingConfig.testCase?.idtest_case || null,
        executeCondition:
          preProcessingConfig.executeCondition === "ifPass"
            ? "pass"
            : preProcessingConfig.executeCondition === "ifFail"
              ? "fail"
              : preProcessingConfig.executeCondition === "always"
                ? "always"
                : "",
      },
      postProcessedTestCase: {
        testCaseId: postProcessingConfig.testCase?.idtest_case || null,
        executeCondition:
          postProcessingConfig.executeCondition === "ifPass"
            ? "pass"
            : postProcessingConfig.executeCondition === "ifFail"
              ? "fail"
              : postProcessingConfig.executeCondition === "always"
                ? "always"
                : "",
      },
    };

    saveTestCaseSettings({ testCaseId: selectedTestCase.idtest_case, payload });
  };

  return (
    <Box>
      <IconContainer handleClick={handleClickOpen}>
        <ScenarioIcon color="#444141" />
      </IconContainer>
      <CustomDialog
        open={open}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleClose}
        aria-describedby="alert-dialog-slide-description"
        maxWidth={false}
      >
        <PageTitle title={"Test Scenario Settings"} variant={"h5"} />
        <DialogContent sx={{ width: "100%", pt: 2 }}>
          <Box display="flex" flexDirection="column" gap={3} width="100%">
            <Box>
              <StyledLabel>Environment</StyledLabel>
              <Box>
                {/* envDetails = ["Dev", "Test", "QA", "Prod"] */}

                {EnvOptions.map((option) => (
                  <PillRadio
                    key={option.value}
                    value={option.value}
                    label={option.label}
                    checked={selectedEnv === option.value}
                    onChange={handleEnvChange}
                  />
                ))}
                {/* {selectedTestCase?.envDetails.map((option) => {
                  <PillRadio 
                  key={option}
                  value={option}
                  label={option}
                  checked={selectedEnv === option}
                  onChange={handleEnvChange}
                  />
                })} */}
              </Box>
            </Box>

            <Box>
              <CustomInput
                labelinput="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                rows={3}
              />
            </Box>

            {!isMobile && (
              <Box>
                <StyledLabel>Browser</StyledLabel>
                <Box>
                  {BrowserOptions.map((option) => (
                    <PillRadio
                      key={option.value}
                      value={option.value}
                      label={option.label}
                      checked={selectedBrowser === option.value}
                      onChange={handleBrowserChange}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {!isMobile && (
              <Box>
                <StyledLabel>Screen Resolution</StyledLabel>
                <Box>
                  {ResolutionOptions.map((option) => (
                    <PillRadio
                      key={option.value}
                      value={option.value}
                      label={option.label}
                      checked={selectedResolution === option.value}
                      onChange={handleResolutionChange}
                    />
                  ))}
                </Box>
              </Box>
            )}

            <Box display="flex" flexWrap="wrap" gap={2}>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={runOnce}
                    onChange={() => setRunOnce((prev) => !prev)}
                  />
                }
                label="Run Once"
                labelPlacement="end"
              />
              {!isMobile && (
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={stealthMode}
                      onChange={() => setStealthMode((prev) => !prev)}
                    />
                  }
                  label="Run In Stealth Mode"
                  labelPlacement="end"
                />
              )}
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={continueTest}
                    onChange={() => setContinueTest((prev) => !prev)}
                  />
                }
                label="Continue Previous Scenario"
                labelPlacement="end"
              />
              {!isMobile && (
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={enableAudio}
                      onChange={() => setEnableAudio((prev) => !prev)}
                    />
                  }
                  label="Enable Audio/Video"
                  labelPlacement="end"
                />
              )}
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={forceNewSession}
                    onChange={() => setForceNewSession((prev) => !prev)}
                  />
                }
                label="Run In New Session"
                labelPlacement="end"
              />
              {!isMobile && (
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={keepBrowserOpen}
                      onChange={() => setKeepBrowserOpen((prev) => !prev)}
                    />
                  }
                  label="Keep Browser Open on Test Step Failure"
                  labelPlacement="end"
                />
              )}
            </Box>

            {!isMobile && (
              <Box display="flex" gap={2}>
                <Box flex={1}>
                  <CustomInput
                    labelinput="Threshold"
                    value={threshold}
                    onChange={(e) => setThreshold(e.target.value)}
                  />
                </Box>
                <Box flex={1}>
                  <CustomInput
                    labelinput="Proxy URL"
                    value={proxyUrl}
                    onChange={(e) => setProxyUrl(e.target.value)}
                  />
                </Box>
              </Box>
            )}

            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Test Case Processing
              </Typography>

              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tabs
                  value={processingTab}
                  onChange={handleTabChange}
                  TabIndicatorProps={{
                    style: {
                      backgroundColor: "#ff5336",
                    },
                  }}
                >
                  <StyledTab label="Pre Processing" />
                  <StyledTab label="Post Processing" />
                </Tabs>
              </Box>

              <TabPanel value={processingTab} index={0}>
                <Box mb={3}>
                  <Typography color="#8089A8" fontSize="13px" fontWeight={500} mb={1}>
                    Select Test Case
                  </Typography>
                  <TestCaseSearch
                    onTestCaseSelect={handlePreProcessingTestCaseSelect}
                    initialValue={preProcessingConfig.testCase}
                    tabId="pre"
                  />
                </Box>

                <Box mb={3}>
                  <Typography color="#8089A8" fontSize="13px" fontWeight={500} mb={1}>
                    Execution Condition
                  </Typography>
                  <FormControl component="fieldset">
                    <RadioGroup
                      value={preProcessingConfig.executeCondition}
                      onChange={handlePreProcessingConditionChange}
                    >
                      <FormControlLabel value="ifPass" control={<Radio />} label="If Pass" />
                      <FormControlLabel value="ifFail" control={<Radio />} label="If Fail" />
                      <FormControlLabel value="always" control={<Radio />} label="Always" />
                    </RadioGroup>
                  </FormControl>
                </Box>

                {selectedTestCase?.preProcessedTestCase?.testCaseId && (
                  <Box
                    mb={3}
                    sx={{
                      p: 2,
                      border: "1px solid #eaeaea",
                      borderRadius: "8px",
                      backgroundColor: "#fafafa",
                    }}
                  >
                    <Typography color="#8089A8" fontSize="13px" fontWeight={500} mb={1}>
                      Current Pre-Processing Test Case
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      <Typography variant="body2">
                        Test Case: {selectedTestCase.preProcessedTestCase.testcasename || "N/A"}
                      </Typography>
                      <Typography variant="body2">
                        ID: {selectedTestCase.preProcessedTestCase.testCaseId}
                      </Typography>
                      <Typography variant="body2">
                        Execute Condition:{" "}
                        {selectedTestCase.preProcessedTestCase.executeCondition || "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </TabPanel>

              <TabPanel value={processingTab} index={1}>
                <Box mb={3}>
                  <Typography color="#8089A8" fontSize="13px" fontWeight={500} mb={1}>
                    Select Test Case
                  </Typography>
                  <TestCaseSearch
                    onTestCaseSelect={handlePostProcessingTestCaseSelect}
                    initialValue={postProcessingConfig.testCase}
                    tabId="post"
                  />
                </Box>

                <Box mb={3}>
                  <Typography color="#8089A8" fontSize="13px" fontWeight={500} mb={1}>
                    Execution Condition
                  </Typography>
                  <FormControl component="fieldset">
                    <RadioGroup
                      value={postProcessingConfig.executeCondition}
                      onChange={handlePostProcessingConditionChange}
                    >
                      <FormControlLabel value="ifPass" control={<Radio />} label="If Pass" />
                      <FormControlLabel value="ifFail" control={<Radio />} label="If Fail" />
                      <FormControlLabel value="always" control={<Radio />} label="Always" />
                    </RadioGroup>
                  </FormControl>
                </Box>

                {selectedTestCase?.postProcessedTestCase?.testCaseId && (
                  <Box
                    mb={3}
                    sx={{
                      p: 2,
                      border: "1px solid #eaeaea",
                      borderRadius: "8px",
                      backgroundColor: "#fafafa",
                    }}
                  >
                    <Typography color="#8089A8" fontSize="13px" fontWeight={500} mb={1}>
                      Current Post-Processing Test Case
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      <Typography variant="body2">
                        Test Case: {selectedTestCase.postProcessedTestCase.testcasename || "N/A"}
                      </Typography>
                      <Typography variant="body2">
                        ID: {selectedTestCase.postProcessedTestCase.testCaseId}
                      </Typography>
                      <Typography variant="body2">
                        Execute Condition:{" "}
                        {selectedTestCase.postProcessedTestCase.executeCondition || "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </TabPanel>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ width: "100%", mt: 2, px: 2 }}>
          <StyledButton
            sx={{ color: "#ff5336" }}
            variant="text"
            color="warning"
            onClick={handleClose}
          >
            Cancel
          </StyledButton>
          <StyledButton
            variant="contained"
            color="warning"
            sx={{
              backgroundColor: "#ff5336",
              color: "#ffffff",
              boxShadow: "0px 16px 30.6px 0px rgba(255, 83, 54, 0.27)",
            }}
            onClick={handleSave}
            endIcon={<BookmarkBorderIcon />}
          >
            Save
          </StyledButton>
        </DialogActions>
      </CustomDialog>
    </Box>
  );
};

export default Settings;
